import { faRotateLeft, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CandidateService } from "Services/candidateService";
import { useEffect, useState, useCallback } from "react";
import { Badge, Button, Card, Form, InputGroup, Modal, Nav, Table, Pagination, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./candidates.css";

export default function CandidatesList() {
    const navigate = useNavigate();
    const [users, setUsers] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(20);
    const [search, setSearch] = useState("");
    const [approvalFilter, setApprovalFilter] = useState("all");
    const [showDeleted, setShowDeleted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState<any>(null);
    const [confirming, setConfirming] = useState<any>(null);
    const [actioning, setActioning] = useState(false);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await CandidateService.getAll(pageNumber, pageSize, search, approvalFilter, showDeleted);
            if (res.status === 200) {
                setUsers(res.data.users);
                setTotal(res.data.total);
            }
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    }, [pageNumber, pageSize, search, approvalFilter, showDeleted]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleSearchChange = (e: any) => {
        if (searchTimeout) clearTimeout(searchTimeout);
        const val = e.target.value;
        setSearchTimeout(setTimeout(() => {
            setSearch(val);
            setPageNumber(1);
        }, 400));
    };

    const switchTab = (deleted: boolean) => {
        if (deleted === showDeleted) return;
        setShowDeleted(deleted);
        setPageNumber(1);
    };

    const handleSetActive = async () => {
        if (!confirming) return;
        setActioning(true);
        try {
            const res = await CandidateService.setActive(confirming._id, showDeleted);
            if (res.status === 200) {
                toast.success(showDeleted ? "Candidate restored" : "Candidate deleted");
                setConfirming(null);
                fetchUsers();
            }
        } catch (e) {
            console.error(e);
            toast.error(showDeleted ? "Failed to restore candidate" : "Failed to delete candidate");
        }
        setActioning(false);
    };

    const totalPages = Math.ceil(total / pageSize);

    return (
        <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">{showDeleted ? "Deleted Candidates" : "All Candidates"}</h5>
                <Badge bg="secondary" className="px-3 py-2">{total} total</Badge>
            </div>

            <Card className="candidates-card">
                <Card.Body>
                    <Nav variant="tabs" activeKey={showDeleted ? "deleted" : "active"} className="mb-3">
                        <Nav.Item>
                            <Nav.Link eventKey="active" onClick={() => switchTab(false)}>Active</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="deleted" onClick={() => switchTab(true)}>Deleted</Nav.Link>
                        </Nav.Item>
                    </Nav>

                    <div className="d-flex gap-3 mb-3 flex-wrap">
                        <InputGroup style={{ maxWidth: 350 }}>
                            <Form.Control
                                placeholder="Search by name, email, reg. number..."
                                onChange={handleSearchChange}
                            />
                        </InputGroup>
                        <Form.Select
                            style={{ maxWidth: 180 }}
                            value={approvalFilter}
                            onChange={(e) => { setApprovalFilter(e.target.value); setPageNumber(1); }}
                        >
                            <option value="all">All Status</option>
                            <option value="approved">Approved</option>
                            <option value="pending">Pending</option>
                            <option value="rejected">Rejected</option>
                        </Form.Select>
                    </div>

                    {showDeleted && (
                        <div className="alert alert-warning py-2 px-3 fs-12">
                            These candidates cannot log in and are hidden from clients and invigilators.
                            Their records are kept and can be restored at any time.
                        </div>
                    )}

                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
                    ) : users.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            {showDeleted ? "No deleted candidates" : "No candidates found"}
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <Table hover className="candidates-table align-middle">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Mobile</th>
                                            <th>Reg. No.</th>
                                            <th>IRIA Member</th>
                                            <th>Qualification</th>
                                            <th>Status</th>
                                            <th className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((user: any, index: number) => (
                                            <tr
                                                key={user._id}
                                                onClick={() => navigate(`/admin-dash/candidates/${user._id}`)}
                                                className="cursor-pointer"
                                            >
                                                <td>{(pageNumber - 1) * pageSize + index + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {user.image ? (
                                                            <img src={user.image} alt="" className="candidate-avatar" />
                                                        ) : (
                                                            <div className="candidate-avatar-placeholder">
                                                                {(user.name || "?")[0].toUpperCase()}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div className="fw-medium">{user.name} {user.last_name || ""}</div>
                                                            <small className="text-muted">{user.city}{user.state ? `, ${user.state}` : ""}</small>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td><small>{user.email}</small></td>
                                                <td><small>{user.mobile || "-"}</small></td>
                                                <td><small className="text-primary">{user.registrationNumber || "-"}</small></td>
                                                <td>
                                                    {user.isIriaMember ? (
                                                        <Badge bg="info" className="badge-sm">Yes</Badge>
                                                    ) : (
                                                        <Badge bg="light" text="dark" className="badge-sm">No</Badge>
                                                    )}
                                                </td>
                                                <td><small>{user.qualification || "-"}</small></td>
                                                <td>
                                                    {user.isApproved ? (
                                                        <Badge bg="success" className="badge-sm">Approved</Badge>
                                                    ) : user.approvalStatus === "REJECTED" ? (
                                                        <Badge bg="danger" className="badge-sm" title={user.rejectionReason || ""}>Rejected</Badge>
                                                    ) : (
                                                        <Badge bg="warning" text="dark" className="badge-sm">Pending</Badge>
                                                    )}
                                                </td>
                                                <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                                    {showDeleted ? (
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="text-success p-0"
                                                            title="Restore candidate"
                                                            onClick={() => setConfirming(user)}
                                                        >
                                                            <FontAwesomeIcon icon={faRotateLeft} />
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="text-danger p-0"
                                                            title="Delete candidate"
                                                            onClick={() => setConfirming(user)}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <small className="text-muted">
                                        Showing {(pageNumber - 1) * pageSize + 1} - {Math.min(pageNumber * pageSize, total)} of {total}
                                    </small>
                                    <Pagination size="sm" className="mb-0">
                                        <Pagination.Prev
                                            disabled={pageNumber === 1}
                                            onClick={() => setPageNumber(pageNumber - 1)}
                                        />
                                        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                            let page: number;
                                            if (totalPages <= 5) {
                                                page = i + 1;
                                            } else if (pageNumber <= 3) {
                                                page = i + 1;
                                            } else if (pageNumber >= totalPages - 2) {
                                                page = totalPages - 4 + i;
                                            } else {
                                                page = pageNumber - 2 + i;
                                            }
                                            return (
                                                <Pagination.Item
                                                    key={page}
                                                    active={page === pageNumber}
                                                    onClick={() => setPageNumber(page)}
                                                >
                                                    {page}
                                                </Pagination.Item>
                                            );
                                        })}
                                        <Pagination.Next
                                            disabled={pageNumber === totalPages}
                                            onClick={() => setPageNumber(pageNumber + 1)}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            <Modal show={!!confirming} onHide={() => setConfirming(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5">
                        {showDeleted ? "Restore candidate" : "Delete candidate"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {showDeleted ? (
                        <>
                            <p className="mb-2">
                                Restore <strong>{confirming?.name} {confirming?.last_name || ""}</strong>?
                            </p>
                            <p className="text-muted fs-12 mb-0">
                                They will be able to log in again and will reappear for clients and invigilators.
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="mb-2">
                                Delete <strong>{confirming?.name} {confirming?.last_name || ""}</strong>?
                            </p>
                            <p className="text-muted fs-12 mb-0">
                                Their record is kept, but they will not be able to log in or take exams,
                                and clients and invigilators will no longer see them. You can restore them
                                from the Deleted tab.
                            </p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" size="sm" onClick={() => setConfirming(null)} disabled={actioning}>
                        Cancel
                    </Button>
                    <Button
                        variant={showDeleted ? "success" : "danger"}
                        size="sm"
                        onClick={handleSetActive}
                        disabled={actioning}
                    >
                        {actioning ? <Spinner animation="border" size="sm" /> : showDeleted ? "Restore" : "Delete"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
